import {
  computed,
  watch,
} from 'vue';

/**
 * Ancho y largo mínimos que tiene que tener el pliego para que entre la imposición
 * (Offset — es el único elemento base que usa este composable).
 *
 * Es el producto por la cantidad de repeticiones, más los márgenes a ambos lados. Función
 * pura para poder fijarla con tests: la fórmula estaba repetida en cada componente de
 * Offset. El ancho suma Laterales a los costados; el largo suma Pinza + Cola en el
 * sentido de alimentación (COTI-633) — no son el mismo margen doblado dos veces, son dos
 * valores distintos que ya representan cada extremo.
 *
 * Gaps (COTI-653): entre repeticiones contiguas va un gap, así que cada eje suma
 * gap * (repeticiones - 1) — GapEje en el ancho, GapDesarrollo en el largo. A diferencia de
 * flexo (donde el gap al desarrollo es un paso por repetición en los metros lineales), en
 * Offset los dos gaps se calculan igual. Esta función es sólo de Offset: Cajas tiene su
 * propia fórmula en price_cajas.vue y no pasa por acá.
 *
 * `ValidarCantidadBocas` es true por defecto (incluso sin dato — elemento nuevo o
 * cargado desde antes de que este campo existiera): las repeticiones reales
 * (EtiquetasEje/EtiquetasDesarrollo) se usan tal cual. En false, se toman como 1 para
 * este cálculo — el valor real que cargó el usuario se sigue guardando y usando para
 * todo lo demás, sólo el mínimo de pliego lo ignora.
 *
 * @param {object} elemento Modelo del elemento.
 * @returns {{ancho: number, largo: number}} En la misma unidad que las medidas del elemento.
 */
export function calcularMinimosPliego(elemento) {
  const validarBocas = elemento?.ValidarCantidadBocas ?? true;

  const repAncho = validarBocas ? Number(elemento?.EtiquetasEje ?? 0) : 1;
  const anchoProd = Number(elemento?.MedidaEje ?? 0);
  const lateralAncho = Number(elemento?.Laterales ?? 0);
  const gapAncho = Number(elemento?.GapEje ?? 0);

  const repLargo = validarBocas ? Number(elemento?.EtiquetasDesarrollo ?? 0) : 1;
  const largoProd = Number(elemento?.MedidaDesarrollo ?? 0);
  const pinza = Number(elemento?.Pinza ?? 0);
  const cola = Number(elemento?.Cola ?? 0);
  const gapLargo = Number(elemento?.GapDesarrollo ?? 0);

  return {
    ancho: anchoProd * repAncho + lateralAncho * 2 + gapAncho * cantidadGaps(repAncho),
    largo: largoProd * repLargo + pinza + cola + gapLargo * cantidadGaps(repLargo),
  };
}

/**
 * Gaps entre `repeticiones` contiguas. Sin repeticiones cargadas (0) no hay gaps: sin el
 * piso en cero, el gap restaría.
 */
function cantidadGaps(repeticiones) {
  return Math.max(0, repeticiones - 1);
}

/**
 * Decide si hay que reescribir los mínimos en el modelo.
 *
 * No alcanza con mirar si el valor calculado cambió, que es lo que hacía el watch original.
 * Hay dos pares de campos en el modelo:
 * - AnchoMinimoHojaCalculado / LargoMinimoHojaCalculado sólo viven en el frontend, no se
 *   persisten. Al cotizar, el elemento se recarga desde la respuesta del backend y vuelve sin
 *   ellos; como las medidas de origen no cambiaron, el cálculo daba lo mismo de antes, el
 *   watch no se disparaba y los campos quedaban en cero en pantalla.
 * - AnchoMinimoMaterial / LargoMinimoMaterial son la copia que se guarda en la base de datos.
 *   Pueden volver desactualizados (elemento guardado antes de que existieran, o con medidas
 *   editadas desde otro lado). El cálculo manda: si difieren, se pisan.
 *
 * Por eso la comparación es contra lo que hay guardado en el modelo, no contra el valor
 * anterior del cálculo.
 *
 * @returns {boolean} true si el modelo quedó desincronizado del cálculo.
 */
export function necesitaSincronizarMinimos(elemento, minimos) {
  return (
    Number(elemento?.AnchoMinimoHojaCalculado) !== minimos.ancho ||
    Number(elemento?.LargoMinimoHojaCalculado) !== minimos.largo ||
    Number(elemento?.AnchoMinimoMaterial) !== minimos.ancho ||
    Number(elemento?.LargoMinimoMaterial) !== minimos.largo
  );
}

/**
 * Escribe los mínimos calculados en los cuatro campos del modelo.
 *
 * Sólo los *HojaCalculado se leen (input_suaje, input_materiales, prices_edit): así el mínimo
 * que ve y valida el usuario sigue siempre al cálculo. Los *MinimoMaterial son sólo salida
 * para persistir y nadie debe leerlos para mostrar ni validar.
 */
export function aplicarMinimos(elemento, minimos) {
  elemento.AnchoMinimoHojaCalculado = minimos.ancho;
  elemento.LargoMinimoHojaCalculado = minimos.largo;
  elemento.AnchoMinimoMaterial = minimos.ancho;
  elemento.LargoMinimoMaterial = minimos.largo;
}

/**
 * Mantiene los mínimos de hoja al día en el modelo (ver `aplicarMinimos`).
 *
 * @param {import('vue').Ref<object>} localModel Modelo del elemento.
 * @param {(modelo: object) => void} updateModelo Emisor del componente.
 */
export function useMinimosPliego(localModel, updateModelo) {
  const minimos = computed(() => calcularMinimosPliego(localModel.value));
  const anchoMinHoja = computed(() => minimos.value.ancho);
  const largoMinHoja = computed(() => minimos.value.largo);

  watch(
    // Los campos del modelo entran en las dependencias justamente para detectar que se
    // perdieron en una recarga; sin ellos el watch no se entera.
    [
      minimos,
      () => localModel.value?.AnchoMinimoHojaCalculado,
      () => localModel.value?.LargoMinimoHojaCalculado,
      () => localModel.value?.AnchoMinimoMaterial,
      () => localModel.value?.LargoMinimoMaterial,
    ],
    () => {
      if (!localModel.value) return;
      if (!necesitaSincronizarMinimos(localModel.value, minimos.value)) return;

      aplicarMinimos(localModel.value, minimos.value);
      updateModelo(localModel.value);
    },
    { immediate: true },
  );

  return { anchoMinHoja, largoMinHoja };
}
